import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

const APP_NAME = 'Brewout Forge'

// Ordered — first matching regex wins. Specific routes before generic.
// Detail pages (e.g. /rma/:id, /products/:sku) get a generic label here; the
// page component itself can override later via the `useDocumentTitle` hook
// below once it has loaded the entity name (e.g. "RMA-2026-0039").
const ROUTE_TITLES = [
  [/^\/rma\/inbox/,              'RMA Inbox'],
  [/^\/rma\/new/,                'New RMA Case'],
  [/^\/rma\/reports/,            'RMA Reports'],
  [/^\/rma\/inquiry-knowledge/,  'Inquiry Knowledge'],
  [/^\/rma\/\d+/,                'RMA Case'],
  // 🔴 E3 (Owner-Entscheid 2026-10-06): der Titel von `/rma` ist „Cases" — der
  // Name des Leisteneintrags (`navItems.jsx:55`), nicht der des Abschnitts. Bis
  // dahin trug der Reiter „After-Sales", waehrend die Seite selbst „Cases" heisst.
  // Zwei Namen fuer EINE Seite sind genau die Fehlerart, gegen die diese Datei
  // gebaut wurde (siehe die `/ops`-Vorgeschichte im Test daneben).
  [/^\/rma$/,                    'Cases'],

  [/^\/purchase_order\/|^\/new$|^\/po-inbox/, 'Purchase Orders'],
  [/^\/overview/,                'Overview'],

  [/^\/products\/attributes/,    'Product Attributes'],
  [/^\/products\/[^/]+\/taxonomy/, 'Product Taxonomy'],
  [/^\/products\/[^/]+/,         'Product'],
  [/^\/products$/,               'Products'],
  [/^\/recommendations/,         'Recommendations'],

  [/^\/vendors\/reports/,        'Vendor Reports'],
  [/^\/vendors\/new/,            'New Vendor'],
  [/^\/vendors\/[^/]+/,          'Vendor'],
  [/^\/vendors$/,                'Vendors'],
  [/^\/vendor-reports\//,        'Vendor Report'],

  [/^\/taxonomy/,                'Taxonomy'],
  [/^\/intelligence\/market-lab/, 'Market Lab'],
  [/^\/competitors\/[^/]+/,      'Competitor'],
  [/^\/competitors$/,             'Competitors'],
  [/^\/scrapers\/new/,           'New Scraper'],
  [/^\/scrapers\/[^/]+\/edit/,   'Edit Scraper'],
  [/^\/scrapers$/,               'Scrapers'],
  [/^\/discovery/,               'Discovery Inbox'],
  [/^\/blog-sources/,            'Blog Sources'],

  [/^\/fulfillment/,             'Fulfillment'],
  [/^\/incoming-inventory/,      'Incoming Inventory'],
  [/^\/kn-shipments/,            'K&N Shipments'],
  [/^\/fca/,                     'FCA'],

  // 🔴 Das Meldungsregister fehlte hier ganz (Regel-16 H5): der Reiter las auf
  // beiden Seiten nur „Brewout Forge". Zwei Eintraege, nicht einer — die
  // Detailseite traegt ihren eigenen, genau wie die uebrigen Detailrouten
  // („RMA Case", „Product", „Vendor"); die genaue Zeile steht VOR der Liste,
  // weil das erste Muster gewinnt.
  [/^\/declarations\/\d+/,       'Declaration'],
  [/^\/declarations\/?$/,        'Declarations'],
  // 🔴 KEIN `/^\/ops/`-Praefix. `/ops` ist seit dem Rueckbau des Daily Board
  // keine Route mehr; ein Praefixeintrag gaebe den drei Unterseiten den
  // Reiter-Titel einer Seite, die es nicht gibt -- und damit zwei Namen fuer
  // dieselbe Seite, acht Pixel voneinander entfernt (Kopfzeile vs. Reiter).
  // Genau davor warnt docs/knowledge/design-language/component-registry.md.
  // Die drei tragen ihre Titel EINZELN; `/ops` selbst faellt auf 'Brewout Forge'
  // zurueck, was fuer eine 404 richtig ist.
  [/^\/ops\/dep-health/,         'Dep Health'],
  [/^\/ops\/lsm-watcher/,        'LSM Watcher'],
  [/^\/ops\/po-anomalies/,       'PO Anomalies'],

  [/^\/admin/,                   'Admin'],
  [/^\/relations-graph/,         'Relations Graph'],
  [/^\/login/,                   'Sign in'],
]

function resolveTitle(pathname) {
  for (const [rx, label] of ROUTE_TITLES) {
    if (rx.test(pathname)) return `${label} · ${APP_NAME}`
  }
  return APP_NAME
}

/**
 * DocumentTitle — watches the current route and updates document.title.
 *
 * Renders null. Drop into App.js inside <BrowserRouter>. Page components that
 * need a more specific title (e.g. "RMA-2026-0039 · Brewout Forge") can call
 * `useDocumentTitle('RMA-2026-0039')` AFTER their data loads; the page-level
 * title wins because its useEffect runs after this one.
 */
export default function DocumentTitle() {
  const loc = useLocation()
  useEffect(() => {
    document.title = resolveTitle(loc.pathname)
  }, [loc.pathname])
  return null
}

/**
 * useDocumentTitle — per-page override hook. Pass a specific label (usually an
 * entity name like a case number or SKU). The effect sets document.title to
 * "${label} · Brewout Forge". Passing a falsy value falls back to the
 * route-based default.
 */
export function useDocumentTitle(label) {
  useEffect(() => {
    if (label) document.title = `${label} · ${APP_NAME}`
  }, [label])
}
